import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  IconBuildingCommunity,
  IconCheck,
  IconSend,
  IconUsersGroup,
  IconX,
} from "@tabler/icons-react";
import { listInvites, listLaunches, respondInvite, type ReceivedLaunch } from "../api";
import { RelayLaunchModal } from "../components/RelayLaunchModal";

const STATUS_LABEL: Record<string, string> = {
  pending: "Aguardando sua resposta",
  accepted: "Aceito",
  declined: "Recusado",
  revoked: "Removido",
};

function formatDate(iso: string) {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString("pt-BR");
}

/**
 * Módulo Lançamentos da imobiliária (hub#753).
 *
 * Duas coisas na mesma tela porque são um fluxo só: primeiro a construtora convida a
 * imobiliária para a **rede de vendas** dela (o vínculo), depois manda os
 * **lançamentos**. Separar em duas telas faria a imobiliária ver "nenhum lançamento"
 * sem descobrir que havia um convite esperando resposta.
 */
export function LaunchesPage() {
  const queryClient = useQueryClient();
  const [relaying, setRelaying] = useState<ReceivedLaunch | null>(null);

  const { data: invites = [], isLoading: loadingInvites } = useQuery({
    queryKey: ["agency-launch-invites"],
    queryFn: listInvites,
  });

  const { data: launches = [], isLoading: loadingLaunches } = useQuery({
    queryKey: ["agency-launches"],
    queryFn: listLaunches,
  });

  const respond = useMutation({
    mutationFn: ({ id, accept }: { id: string; accept: boolean }) => respondInvite(id, accept),
    onSuccess: (_result, { accept }) => {
      toast.success(accept ? "Convite aceito." : "Convite recusado.");
      void queryClient.invalidateQueries({ queryKey: ["agency-launch-invites"] });
      // O aceite libera os lançamentos daquela construtora — sem invalidar as duas
      // listas, a de baixo continuaria mostrando o estado anterior.
      void queryClient.invalidateQueries({ queryKey: ["agency-launches"] });
    },
    onError: () => toast.error("Não foi possível responder o convite."),
  });

  const pending = invites.filter((i) => i.status === "pending");

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Lançamentos</h1>
        <p className="mt-0.5 text-[13px] text-slate-500">
          Empreendimentos que as construtoras enviaram para a sua imobiliária.
        </p>
      </div>

      {/* Convites primeiro: enquanto não houver aceite, não chega lançamento nenhum
          daquela construtora — mostrar a lista vazia sem explicar o porquê deixaria a
          imobiliária esperando material que não vem. */}
      {pending.length > 0 && (
        <section className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4">
          <h2 className="flex items-center gap-1.5 text-[13px] font-semibold text-amber-900">
            <IconUsersGroup size={15} />
            Convites de parceria
          </h2>
          <p className="mt-0.5 text-[12px] text-amber-800">
            Aceitar entra na rede de vendas da construtora e libera os lançamentos dela.
          </p>
          <ul className="mt-3 space-y-2">
            {pending.map((invite) => (
              <li
                key={invite.id}
                className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-white px-3 py-2"
              >
                <span className="min-w-0 flex-1 truncate text-[13px] font-medium">
                  {invite.developerName}
                </span>
                <span className="text-[11.5px] text-slate-500">
                  Convidada em {formatDate(invite.invitedAt)}
                </span>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => respond.mutate({ id: invite.id, accept: true })}
                    disabled={respond.isPending}
                    className="flex items-center gap-1 rounded-lg bg-slate-900 px-2.5 py-1 text-[12.5px] font-medium text-white hover:bg-slate-800 disabled:opacity-50"
                  >
                    <IconCheck size={14} />
                    Aceitar
                  </button>
                  <button
                    type="button"
                    onClick={() => respond.mutate({ id: invite.id, accept: false })}
                    disabled={respond.isPending}
                    className="flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1 text-[12.5px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    <IconX size={14} />
                    Recusar
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section>
        {loadingInvites || loadingLaunches ? (
          <div className="h-40 animate-pulse rounded-2xl bg-slate-100" />
        ) : launches.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center">
            <IconBuildingCommunity size={28} className="mx-auto text-slate-300" />
            <p className="mt-2 text-[13px] text-slate-500">
              {pending.length > 0
                ? "Aceite um convite acima para começar a receber lançamentos."
                : "Nenhuma construtora enviou lançamentos ainda. Passe o código da sua imobiliária para as construtoras com quem você trabalha."}
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/60 text-left text-[11px] uppercase tracking-wide text-slate-500">
                    <th className="px-4 py-2.5 font-semibold">Lançamento</th>
                    <th className="px-4 py-2.5 font-semibold">Construtora</th>
                    <th className="px-4 py-2.5 font-semibold">Praça</th>
                    <th className="px-4 py-2.5 font-semibold">Situação</th>
                    <th className="px-4 py-2.5 font-semibold">Repassado</th>
                    <th className="px-4 py-2.5" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {launches.map((launch) => (
                    <tr key={launch.inviteId} className="transition-colors hover:bg-slate-50/60">
                      <td className="px-4 py-3 font-medium">{launch.launchName}</td>
                      <td className="px-4 py-3 text-slate-600">{launch.developerName}</td>
                      <td className="px-4 py-3 text-slate-600">
                        {launch.city}
                        {launch.state ? `/${launch.state}` : ""}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={
                            "rounded-md px-1.5 py-0.5 text-[10.5px] font-semibold " +
                            (launch.status === "accepted"
                              ? "bg-emerald-50 text-emerald-700"
                              : launch.status === "pending"
                                ? "bg-amber-50 text-amber-700"
                                : "bg-slate-100 text-slate-500")
                          }
                        >
                          {STATUS_LABEL[launch.status] ?? launch.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 tabular-nums text-slate-600">
                        {/* Se zerado, o material parou aqui — é o que mostra à
                            imobiliária que ela recebeu e não distribuiu. */}
                        {launch.relayedTo > 0 ? (
                          `${launch.relayedTo} corretor${launch.relayedTo > 1 ? "es" : ""}`
                        ) : (
                          <span className="text-slate-400">ninguém ainda</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {launch.status === "pending" ? (
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              type="button"
                              onClick={() => respond.mutate({ id: launch.inviteId, accept: true })}
                              disabled={respond.isPending}
                              className="rounded-lg bg-slate-900 px-2.5 py-1 text-[12.5px] font-medium text-white hover:bg-slate-800 disabled:opacity-50"
                            >
                              Aceitar
                            </button>
                            <button
                              type="button"
                              onClick={() => respond.mutate({ id: launch.inviteId, accept: false })}
                              disabled={respond.isPending}
                              className="rounded-lg border border-slate-200 px-2.5 py-1 text-[12.5px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                            >
                              Recusar
                            </button>
                          </div>
                        ) : launch.status === "accepted" ? (
                          <button
                            type="button"
                            title="Repassar ao time"
                            aria-label={`Repassar ${launch.launchName} ao time`}
                            onClick={() => setRelaying(launch)}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1 text-[12.5px] font-medium text-slate-700 hover:bg-slate-50"
                          >
                            <IconSend size={14} />
                            Repassar
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {relaying && <RelayLaunchModal launch={relaying} onClose={() => setRelaying(null)} />}
    </div>
  );
}
